import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useLayoutEffect, useRef } from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { useAppSelector } from '../../app/hooks';
import {
  selectFeatureFlagPosCart,
  selectIsShadowModeEnabled,
} from '../../redux/features/config/config.selector';
import {
  selectCanPromoteToOrder,
  selectShouldShowMyCart,
} from '../../redux/features/agentMode/agentMode.selector';
import { selectIsDraftCartEmpty } from '../../redux/features/draftCart/draftCart.selector';
import Colors from '../../utils/color';
import {
  getRightStackHeight,
  saveRightStackHeight,
} from '../../utils/local-storage';
import {
  CARD_MAX_HEIGHT,
  CARD_MIN_HEIGHT,
} from '../Layout/LayoutPreferenceDialog';
import { OrderActions } from '../OrderReview/OrderActions';
import AgentCartCard from './AgentCartCard';
import MyCartCard from './MyCartCard';
import OrderTotalCard from './OrderTotalCard';
import PosCartCard from './PosCartCard';
import useRightStackCollapse from './hooks/useRightStackCollapse.hooks';
import useStackLayout from './hooks/useStackLayout.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Right pane wrapper (mirrors LeftSidebar's leftCard): a flush column holding
  // the Agent Cart / POS Cart stack + the pinned totals / Cancel-Finish footer.
  // No outer frame or padding — sections attach edge-to-edge.
  outer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Holds the resizable card stack; the footer sits below it (pinned).
  panelRegion: {
    flex: 1,
    minHeight: 0,
  },
  // Wraps each card inside a vertical panel: fills the panel height so each
  // card's OWN internal scroll works; overflow hidden (not auto) so no outer
  // scrollbar appears beside a collapsed hug-height card.
  stackPane: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'hidden',
  },
  // Hairline divider between the Agent Cart / POS Cart cards (mirrors
  // LeftSidebar's vResizeHandle): a 1px rule with NO white gutter so the card
  // headers butt straight up against the line. The drag hit area (invisible
  // ::before) and grip pill (::after) live in pseudo-elements so the visible
  // line stays 1px while staying easy to grab.
  vResizeHandle: {
    position: 'relative',
    zIndex: 1,
    height: 1,
    flexShrink: 0,
    outline: 'none',
    backgroundColor: Colors.alto,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: spacing(-0.75),
      bottom: spacing(-0.75),
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
      height: 3,
      width: spacing(3.5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease, width 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
      width: spacing(5),
    },
  },
  // HITL-1614: the horizontal twin of vResizeHandle, dividing My Cart from the
  // AI cart. Same 1px rule + invisible hit area + grip pill, rotated.
  hResizeHandle: {
    position: 'relative',
    zIndex: 1,
    width: 1,
    flexShrink: 0,
    outline: 'none',
    backgroundColor: Colors.alto,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: spacing(-0.75),
      right: spacing(-0.75),
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
      width: 3,
      height: spacing(3.5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease, height 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
      height: spacing(5),
    },
  },
  // Each column inside the horizontal split; mirrors stackPane so a card's own
  // internal scroll still works and no outer scrollbar appears.
  columnPane: {
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // Pinned footer: subtotal / tax / total + Cancel / Finish. Sits BELOW the
  // resizable stack so it stays visible however the cards are resized or
  // collapsed — the whole point of splitting the carts into their own panels.
  // Flush like the sections above: attached by a single top rule, no card frame.
  footer: {
    flexShrink: 0,
    backgroundColor: Colors.white,
    borderTop: `1px solid ${Colors.alto}`,
    overflow: 'hidden',
  },
}));

interface RightSidebarProps {
  // Per-agent storage key; the nested vertical stack persists under a suffix.
  paneStorageKey: string;
}

/**
 * RIGHT SIDEBAR of the HITL console. A drag-to-resizable vertical stack of the
 * Agent Cart and POS Cart cards (sizes persist per agent via autoSaveId, the
 * vertical twin of LeftSidebar), with the order totals + Cancel / Finish actions
 * pinned in a footer below the stack so they never scroll away. The POS Cart
 * card is present only when the pos_cart flag is on. Collapse-sync between each
 * card's chevron and its panel lives in useRightStackCollapse.
 */
const RightSidebar = ({ paneStorageKey }: RightSidebarProps) => {
  const classes = useStyles();
  const isPosCartEnabled = useAppSelector(selectFeatureFlagPosCart);
  // HITL-1614: the flag decides whether a second column EXISTS at all; the mode
  // decides only whether it is currently on screen. With `hitl_shadow_mode` off
  // no nested PanelGroup is constructed and this file renders exactly as it does
  // in production today.
  const isShadowModeEnabled = useAppSelector(selectIsShadowModeEnabled);
  const shouldShowMyCart = useAppSelector(selectShouldShowMyCart);
  // HITL-1615: once My Cart is empty in `Intercept` the agent has promoted
  // everything, so the column gives its width back. It COLLAPSES — the panel
  // stays mounted and is resized to zero. Unmounting a sibling would risk
  // remounting the panel that holds `OrderReview`, whose mount effect dispatches
  // `sendOrder` unguarded, and `Intercept` is the one mode where that sink is
  // open. A layout decision must never put an order on the POS.
  const canPromoteToOrder = useAppSelector(selectCanPromoteToOrder);
  const isDraftCartEmpty = useAppSelector(selectIsDraftCartEmpty);
  const shouldCollapseMyCart = canPromoteToOrder && isDraftCartEmpty;
  const myCartPanelRef = useRef<ImperativePanelHandle>(null);

  // useLayoutEffect, matching useRightStackCollapse: apply the resize in the
  // same paint as the render that caused it, so promoting the last line does not
  // read as a two-phase animation.
  useLayoutEffect(() => {
    const panel = myCartPanelRef.current;
    if (!panel) return;
    if (shouldCollapseMyCart && panel.isExpanded()) panel.collapse();
    else if (!shouldCollapseMyCart && panel.isCollapsed()) panel.expand();
  }, [shouldCollapseMyCart]);

  const {
    agentOpen,
    setAgentOpen,
    posOpen,
    setPosOpen,
    agentPanelRef,
    posPanelRef,
  } = useRightStackCollapse();
  // Card-height persistence + live dialog control for this stack. POS Cart opens
  // at the default [75, 25] split; when a saved split exists, restoreOnMount
  // applies it over that default on mount.
  const { groupRef, reportSizes, persistOnDragEnd } = useStackLayout({
    region: 'right',
    getSaved: getRightStackHeight,
    saveSaved: saveRightStackHeight,
    staleAutoSaveKey: `react-resizable-panels:${paneStorageKey}:right-stack`,
    restoreOnMount: true,
    min: CARD_MIN_HEIGHT,
    max: CARD_MAX_HEIGHT,
  });

  return (
    <div className={classes.outer}>
      <div className={classes.panelRegion}>
        {/* Heights persist per agent via the dedicated `hitl-layout-height-right`
            key (drag via persistOnDragEnd, dialog via Apply) — no autoSaveId, so
            there's a single source of truth. */}
        <PanelGroup direction="vertical" ref={groupRef} onLayout={reportSizes}>
          <Panel
            id="agent-cart"
            order={1}
            ref={agentPanelRef}
            collapsible
            collapsedSize={8}
            defaultSize={isPosCartEnabled ? 75 : 100}
            minSize={15}
            onCollapse={() => setAgentOpen(false)}
            onExpand={() => setAgentOpen(true)}
          >
            <div className={classes.stackPane}>
              {isShadowModeEnabled ? (
                /*
                  HITL-1614 — My Cart | AI Cart.

                  The AI-cart Panel is NEVER conditionally mounted. It holds
                  OrderReview, whose mount effect at OrderReview.tsx:174
                  dispatches `sendOrder` with no `validCartItems.length` guard —
                  so remounting it in `Intercept` would put an order on the POS
                  as a side effect of a layout decision. That is the shape of the
                  dropped-order incident, and it is why the mode changes pane
                  SIZES here and never the tree.

                  The `&&` below yields `false` in one child slot rather than
                  removing a child, so the AI Panel keeps its position in the
                  children array across a mode switch and React reconciles it in
                  place. `order` is set on both for react-resizable-panels' own
                  conditional-panel bookkeeping.

                  The split is intentionally NOT persisted. `paneStorageKey` is
                  not a persistence mechanism — it only names a STALE
                  react-resizable-panels autosave to delete; real persistence
                  runs through LayoutPreferenceContext, whose `LayoutRegion` is a
                  closed union backing two Record initialisers, and widening it
                  also means a new key in local-storage and a row in the Layout
                  preference dialog. Out of scope here; the split opens even each
                  session.
                */
                <PanelGroup direction="horizontal">
                  {shouldShowMyCart && (
                    <>
                      <Panel
                        id="my-cart"
                        order={1}
                        ref={myCartPanelRef}
                        collapsible
                        collapsedSize={0}
                        defaultSize={50}
                        minSize={20}
                      >
                        <div className={classes.columnPane}>
                          <MyCartCard />
                        </div>
                      </Panel>
                      <PanelResizeHandle className={classes.hResizeHandle} />
                    </>
                  )}
                  <Panel
                    id="ai-cart"
                    order={2}
                    defaultSize={shouldShowMyCart ? 50 : 100}
                    minSize={20}
                  >
                    <div className={classes.columnPane}>
                      <AgentCartCard
                        open={agentOpen}
                        onOpenChange={setAgentOpen}
                      />
                    </div>
                  </Panel>
                </PanelGroup>
              ) : (
                <AgentCartCard open={agentOpen} onOpenChange={setAgentOpen} />
              )}
            </div>
          </Panel>

          {isPosCartEnabled && (
            <>
              <PanelResizeHandle
                className={classes.vResizeHandle}
                onDragging={persistOnDragEnd}
              />
              <Panel
                id="pos-cart"
                order={2}
                ref={posPanelRef}
                collapsible
                collapsedSize={8}
                defaultSize={25}
                minSize={15}
                onCollapse={() => setPosOpen(false)}
                onExpand={() => setPosOpen(true)}
              >
                <div className={classes.stackPane}>
                  <PosCartCard open={posOpen} onOpenChange={setPosOpen} />
                </div>
              </Panel>
            </>
          )}
        </PanelGroup>
      </div>

      {/* Pinned footer — totals + Cancel / Finish, always visible. */}
      <div className={classes.footer}>
        <OrderTotalCard />
        <OrderActions />
      </div>
    </div>
  );
};

export default RightSidebar;
